import React, { useState } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { ArrowLeftRight, Plus, Minus, Search, X, Check } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { Menu } from "@headlessui/react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import TravelAutocompleteInput from './TravelAutocompleteInput';
import { toast } from 'react-toastify';
import './SearchHeader.css';

import Logo from '../common/Logo';

const SearchHeader = ({
    origin,
    setOrigin,
    originData,
    setOriginData,
    destination,
    setDestination,
    destinationData,
    setDestinationData,
    travelDate,
    setTravelDate,
    travelerCounts,
    setTravelerCounts,
    selectedSeatClasses,
    setSelectedSeatClasses,
    selectedModes = [],
    setSelectedModes,
    nonStopOnly,
    setNonStopOnly,
    handleSearch,
    isLoading
}) => {
    const navigate = useNavigate();
    const location = useLocation();
    const { userProfile, openLoginModal, handleLogout: logoutFromContext } = useAuth();

    const handleLogout = async () => {
        try {
            await logoutFromContext();
            window.location.reload();
            navigate("/");
        } catch (error) {
            console.error("Logout error:", error);
        }
    };
    const [showTravelersModal, setShowTravelersModal] = useState(false);
    const [showModesModal, setShowModesModal] = useState(false);
    const [selectedModesLocal, setSelectedModesLocal] = useState(selectedModes);
    const [isOriginSelected, setIsOriginSelected] = useState(!!origin);
    const [isDestinationSelected, setIsDestinationSelected] = useState(!!destination);
    // Local (uncommitted) checkbox state — only applied to parent on Search click
    const [pendingNonStop, setPendingNonStop] = useState(nonStopOnly);
    const [progress, setProgress] = useState(0);
    const [visible, setVisible] = useState(false);

    const modalRef = React.useRef(null);
    const modesModalRef = React.useRef(null);

    const getModeDisplay = () => {
        if (selectedModes.includes('All')) return 'All';
        if (selectedModes.length > 1) return 'Multiple';
        return selectedModes[0] || 'Flight';
    };

    React.useEffect(() => {
        const handleClickOutside = (event) => {
            if (showTravelersModal && modalRef.current && !modalRef.current.contains(event.target)) {
                setShowTravelersModal(false);
            }
            if (showModesModal && modesModalRef.current && !modesModalRef.current.contains(event.target)) {
                if (selectedModesLocal.length === 0) {
                    if (!toast.isActive('mode-warning')) {
                        toast.error('Please select at least one travel mode', {
                            position: 'top-center',
                            toastId: 'mode-warning'
                        });
                    }
                } else {
                    setShowModesModal(false);
                }
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [showTravelersModal, showModesModal, selectedModesLocal]);

    // Sync local state when external modes change
    React.useEffect(() => {
        setSelectedModesLocal(selectedModes);
    }, [selectedModes]);

    // Sync pendingNonStop if the parent resets nonStopOnly (e.g. on mount from URL state)
    React.useEffect(() => {
        setPendingNonStop(nonStopOnly);
    }, [nonStopOnly]);

    // Simulated progress logic
    React.useEffect(() => {
        let interval;
        if (isLoading) {
            setVisible(true);
            setProgress(0);
            interval = setInterval(() => {
                setProgress(prev => {
                    if (prev < 30) return prev + Math.random() * 10;
                    if (prev < 60) return prev + Math.random() * 5;
                    if (prev < 90) return prev + Math.random() * 1;
                    if (prev < 98) return prev + 0.1;
                    return prev;
                });
            }, 200);
        } else {
            // If isLoading is false, we clear everything
            if (progress > 0) {
                setProgress(100);
                const timeout = setTimeout(() => {
                    setVisible(false);
                    setProgress(0);
                }, 500);
                return () => clearTimeout(timeout);
            } else {
                setVisible(false);
            }
        }
        return () => clearInterval(interval);
    }, [isLoading]);

    const handleSwap = () => {
        const tempOrigin = origin;
        const tempOriginData = originData;
        const tempOriginSelected = isOriginSelected;

        setOrigin(destination);
        setOriginData(destinationData);
        setIsOriginSelected(isDestinationSelected);

        setDestination(tempOrigin);
        setDestinationData(tempOriginData);
        setIsDestinationSelected(tempOriginSelected);
    };

    const getTravelersSummary = () => {
        const adults = travelerCounts.adults;
        const children = travelerCounts.children;
        const infants = travelerCounts.infants;

        let parts = [];
        if (adults > 0) parts.push(`${adults} adult${adults > 1 ? 's' : ''}`);
        if (children > 0) parts.push(`${children} child${children > 1 ? 'ren' : ''}`);
        if (infants > 0) parts.push(`${infants} infant${infants > 1 ? 's' : ''}`);

        const travelersText = parts.length > 0 ? parts.join(', ') : '1 adult';
        const filteredClasses = selectedSeatClasses.filter(c => {
            if (['1A', '2A', '3A', 'SL'].includes(c)) return selectedModes.includes('Train') || selectedModes.includes('All');
            if (['Economy', 'Business', 'First'].includes(c)) return selectedModes.includes('Flight') || selectedModes.includes('All');
            return true;
        });

        const travelersClassesText = filteredClasses.length > 0 ? filteredClasses.join(', ') : 'Economy';
        return `${travelersText}, ${travelersClassesText}`;
    };

    const toggleSeatClass = (cls) => {
        setSelectedSeatClasses(prev => {
            const current = Array.isArray(prev) ? prev : [];
            const isTrainClass = ['1A', '2A', '3A', 'SL'].includes(cls);
            const isFlightClass = ['Economy', 'Business', 'First'].includes(cls);

            if (isTrainClass) {
                // Filter out other train classes, then add this one
                const withoutTrain = current.filter(c => !['1A', '2A', '3A', 'SL'].includes(c));
                return [...withoutTrain, cls];
            }
            if (isFlightClass) {
                // Filter out other flight classes, then add this one
                const withoutFlight = current.filter(c => !['Economy', 'Business', 'First'].includes(c));
                return [...withoutFlight, cls];
            }
            return current;
        });
    };

    const handleModeToggle = (mode) => {
        setSelectedModesLocal(prev => {
            if (mode === 'All') {
                return prev.includes('All') ? [] : ['All'];
            }

            const newModes = prev.filter(m => m !== 'All');
            if (newModes.includes(mode)) {
                return newModes.filter(m => m !== mode);
            } else {
                return [...newModes, mode];
            }
        });
    };

    const handleModeApply = () => {
        if (!selectedModesLocal || selectedModesLocal.length === 0) {
            toast.error('Please select at least one travel mode', { position: 'top-center' });
            return;
        }
        setSelectedModes(selectedModesLocal);
        setShowModesModal(false);
    };

    const handleModeClear = () => {
        setSelectedModesLocal(['All']);
    };

    const passengerTypes = [
        { key: 'adults', label: 'Adults', age: '18+' },
        { key: 'children', label: 'Children', age: '2-11' },
        { key: 'infants', label: 'Infants', age: 'on lap' }
    ];

    const modes = [
        { id: 'All', label: 'All' },
        { id: 'Train', label: 'Train' },
        { id: 'Flight', label: 'Flight' },
        // { id: 'Bus', label: 'Bus' }
    ];

    const renderNavbarProfile = (isMobile = false) => {
        if (!userProfile) return null;

        if (userProfile.code === 2) {
            return (
                <div className={`user-profile-wrapper ${isMobile ? 'mobile-profile' : ''}`} style={{ position: "relative" }}>
                    <Menu as="div" className="user-dropdown">
                        <Menu.Button style={{
                            background: "transparent",
                            border: "none",
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                            padding: "4px 8px",
                            borderRadius: "8px"
                        }} className="hover:bg-gray-50 transition-colors">
                            {userProfile.photoURL && userProfile.photoURL !== '/profileDefault.png' ? (
                                <img
                                    src={userProfile.photoURL}
                                    alt="Profile"
                                    style={{ width: "32px", height: "32px", borderRadius: "50%", objectFit: "cover" }}
                                    onError={(e) => {
                                        e.target.style.display = 'none';
                                        e.target.parentElement.querySelector('.avatar-fallback').style.display = 'flex';
                                    }}
                                />
                            ) : null}
                            <div
                                className="avatar-fallback"
                                style={{
                                    width: "32px",
                                    height: "32px",
                                    borderRadius: "50%",
                                    backgroundColor: "#FF5B15",
                                    color: "white",
                                    display: (userProfile.photoURL && userProfile.photoURL !== '/profileDefault.png') ? 'none' : 'flex',
                                    alignItems: "center",
                                    justifyContent: "center",
                                    fontSize: "14px",
                                    fontWeight: "600",
                                    textTransform: "uppercase"
                                }}
                            >
                                {userProfile.displayName?.charAt(0) || 'U'}
                            </div>
                        </Menu.Button>
                        <Menu.Items style={{
                            position: "absolute",
                            right: 0,
                            marginTop: "4px",
                            width: "180px",
                            backgroundColor: "white",
                            border: "1px solid #E5E7EB",
                            borderRadius: "8px",
                            boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
                            padding: "4px 0",
                            zIndex: 2200
                        }}>
                            <Menu.Item>
                                {({ active }) => (
                                    <Link
                                        to="/booking-history"
                                        style={{
                                            display: "block",
                                            padding: "8px 16px",
                                            fontSize: "14px",
                                            color: "#374151",
                                            textDecoration: "none",
                                            backgroundColor: active ? "#F9FAFB" : "transparent"
                                        }}
                                    >
                                        My Bookings
                                    </Link>
                                )}
                            </Menu.Item>
                            <Menu.Item>
                                {({ active }) => (
                                    <button
                                        onClick={handleLogout}
                                        style={{
                                            width: "100%",
                                            textAlign: "left",
                                            padding: "8px 16px",
                                            fontSize: "14px",
                                            color: "#EF4444",
                                            border: "none",
                                            backgroundColor: active ? "#F9FAFB" : "transparent",
                                            cursor: "pointer"
                                        }}
                                    >
                                        Logout
                                    </button>
                                )}
                            </Menu.Item>
                        </Menu.Items>
                    </Menu>
                </div>
            );
        } else if (userProfile.code === 3) {
            return (
                <button
                    className="hover:opacity-90 shadow-sm sign-in-btn"
                    style={{
                        background: "#FF5B15",
                        color: "#fff",
                        border: "none",
                        borderRadius: "8px",
                        padding: "0 24px",
                        height: "44.8px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontWeight: "600",
                        cursor: "pointer",
                        fontSize: "14px",
                        transition: "all 0.2s",
                        marginBottom: "0px",
                        marginTop: "0px"
                    }}
                    onClick={openLoginModal}
                >
                    Sign In
                </button>
            );
        }
        return null;
    };

    return (
        <nav className="search-navbar">
            {visible && (
                <div className="header-loading-bar-container">
                    <div
                        className="header-loading-bar-fill"
                        style={{
                            width: `${progress}%`,
                            transition: progress === 100 ? 'width 0.4s ease-out' : 'width 0.6s ease-out'
                        }}
                    ></div>
                </div>
            )}

            <div className="search-nav-container">
                <div className="header-top-logo-row">
                    <Logo />

                    {/* Mobile/Tablet Avatar - Hidden on desktop */}
                    <div className="mobile-profile-container">
                        {renderNavbarProfile(true)}
                    </div>
                </div>


                {/* Search Section Wrapper */}
                <div className={`header-search-section-wrap ${visible ? 'loading-disabled' : ''}`}>
                    {/* Search Form */}
                    {/* Search Form */}
                    <div className="header-search-form">
                        <div className="header-search-main">
                            <div className="header-main-card">
                                <div className="header-locations-row">
                                    <div className="header-search-field">
                                        {isOriginSelected && origin ? (
                                            <div className="location-tag">
                                                <span className="location-name">{origin}</span>
                                                <button className="tag-clear-btn" onClick={() => {
                                                    setOrigin("");
                                                    if (setOriginData) setOriginData(null);
                                                    setIsOriginSelected(false);
                                                }}>
                                                    <X size={14} />
                                                </button>
                                            </div>
                                        ) : (
                                            <TravelAutocompleteInput
                                                className="header-search-input-field"
                                                placeholder="From"
                                                value={origin}
                                                maxLength={50}
                                                onChange={(val) => {
                                                    setOrigin(val);
                                                    setIsOriginSelected(false);
                                                }}
                                                onSelect={(item) => {
                                                    setOrigin(item.label);
                                                    if (setOriginData) setOriginData(item);
                                                    setIsOriginSelected(true);
                                                }}
                                            />
                                        )}
                                    </div>

                                    <div className="header-swap-btn" onClick={handleSwap}>
                                        <ArrowLeftRight size={16} color="#475569" />
                                    </div>

                                    <div className="header-search-field">
                                        {isDestinationSelected && destination ? (
                                            <div className="location-tag">
                                                <span className="location-name">{destination}</span>
                                                <button className="tag-clear-btn" onClick={() => {
                                                    setDestination("");
                                                    if (setDestinationData) setDestinationData(null);
                                                    setIsDestinationSelected(false);
                                                }}>
                                                    <X size={14} />
                                                </button>
                                            </div>
                                        ) : (
                                            <TravelAutocompleteInput
                                                className="header-search-input-field"
                                                placeholder="To"
                                                value={destination}
                                                maxLength={50}
                                                onChange={(val) => {
                                                    setDestination(val);
                                                    setIsDestinationSelected(false);
                                                }}
                                                onSelect={(item) => {
                                                    setDestination(item.label);
                                                    if (setDestinationData) setDestinationData(item);
                                                    setIsDestinationSelected(true);
                                                }}
                                            />
                                        )}
                                    </div>
                                </div>

                                <div className="header-card-divider mobile-only"></div>

                                <div className="header-details-row">
                                    <div className="header-search-sep desktop-only"></div>

                                    <div className="header-search-field date-field">
                                        <div className="datepicker-wrapper">
                                            <DatePicker
                                                selected={travelDate}
                                                onChange={(date) => setTravelDate(date)}
                                                dateFormat="MMM dd/eee"
                                                className="header-search-input-field"
                                                placeholderText="Date"
                                                minDate={new Date(new Date().setHours(0, 0, 0, 0))}
                                            />
                                        </div>
                                    </div>

                                    <div className="header-search-sep"></div>

                                    <div className="header-search-field travelers-field" onClick={() => setShowTravelersModal(!showTravelersModal)}>
                                        <div className="header-travelers-display" style={{ cursor: 'pointer' }}>
                                            {getTravelersSummary()}
                                        </div>
                                    </div>

                                    <div className="header-search-sep desktop-only"></div>

                                    <div className="header-search-field modes-field" onClick={() => setShowModesModal(!showModesModal)}>
                                        <div className="header-travelers-display" style={{ cursor: 'pointer' }}>
                                            {getModeDisplay()}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {showTravelersModal && (
                            <div className="header-travelers-dropdown" ref={modalRef} onClick={(e) => e.stopPropagation()}>
                                <h2 className="header-modal-title">Travelers</h2>
                                {passengerTypes.map(({ key, label, age }) => (
                                    <div className="header-traveler-row" key={key}>
                                        <div className="header-type-info">
                                            <h4>{label}</h4>
                                            <span>{age}</span>
                                        </div>
                                        <div className="header-counter">
                                            <button
                                                className="header-count-btn"
                                                onClick={() => setTravelerCounts(prev => ({ ...prev, [key]: Math.max(0, (prev[key] || 0) - 1) }))}
                                            >
                                                <Minus size={14} />
                                            </button>
                                            <span>{travelerCounts[key] || 0}</span>
                                            <button
                                                className="header-count-btn"
                                                onClick={() => setTravelerCounts(prev => ({ ...prev, [key]: (prev[key] || 0) + 1 }))}
                                            >
                                                <Plus size={14} />
                                            </button>
                                        </div>
                                    </div>
                                ))}

                                <div className="header-seat-classes">
                                    {(selectedModes.includes('Flight') || selectedModes.includes('All')) && (
                                        <>
                                            <h2 className="header-modal-title" style={{ fontSize: '16px', marginTop: '10px' }}>Flight Seat Class</h2>
                                            <div className="header-classes-grid">
                                                {['Economy', 'Business', 'First'].map(cls => (
                                                    <div
                                                        key={cls}
                                                        className={`header-class-tag ${selectedSeatClasses.includes(cls) ? 'active' : ''}`}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            toggleSeatClass(cls);
                                                        }}
                                                    >
                                                        {cls}
                                                    </div>
                                                ))}
                                            </div>
                                        </>
                                    )}

                                    {(selectedModes.includes('Train') || selectedModes.includes('All')) && (
                                        <>
                                            <h2 className="header-modal-title" style={{ fontSize: '16px', marginTop: '10px' }}>Train Seat Class</h2>
                                            <div className="header-classes-grid">
                                                {['1A', '2A', '3A', 'SL'].map(cls => (
                                                    <div
                                                        key={cls}
                                                        className={`header-class-tag ${selectedSeatClasses.includes(cls) ? 'active' : ''}`}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            toggleSeatClass(cls);
                                                        }}
                                                    >
                                                        {cls}
                                                    </div>
                                                ))}
                                            </div>
                                        </>
                                    )}
                                </div>

                                <div className="header-modal-actions">
                                    <button className="header-clear-btn" onClick={() => {
                                        setTravelerCounts({ adults: 1, children: 0, infants: 0 });
                                        setSelectedSeatClasses(['Economy', '3A']);
                                    }}>Clear</button>
                                    <button className="header-apply-btn" onClick={() => setShowTravelersModal(false)}>Apply</button>
                                </div>
                            </div>
                        )}

                        {showModesModal && (
                            <div className="header-travelers-dropdown header-modes-dropdown" ref={modesModalRef} onClick={(e) => e.stopPropagation()}>
                                <h2 className="header-modal-title">Select mode</h2>
                                <div className="header-modes-list">
                                    {modes.map(mode => (
                                        <div
                                            key={mode.id}
                                            className="header-mode-row"
                                            onClick={() => handleModeToggle(mode.id)}
                                        >
                                            <span className="header-mode-label">{mode.label}</span>
                                            <div className={`header-mode-checkbox ${selectedModesLocal.includes(mode.id) ? 'checked' : ''}`}>
                                                {selectedModesLocal.includes(mode.id) && <Check size={14} color="#FF5B15" strokeWidth={3} />}
                                            </div>
                                        </div>
                                    ))}
                                </div>

                                <div className="header-modal-actions" style={{ marginTop: '24px' }}>
                                    <button className="header-clear-btn" onClick={handleModeClear}>Clear</button>
                                    <button className="header-apply-btn" onClick={handleModeApply}>Apply</button>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Header Actions */}
                    <div className="header-actions-container">
                        <label className="non-stop-checkbox-wrapper" onClick={() => setPendingNonStop(prev => !prev)}>
                            <div className={`custom-checkbox ${pendingNonStop ? 'checked' : ''}`}>
                                {pendingNonStop && <Check size={14} color="white" strokeWidth={3} />}
                            </div>
                            <span>Non stop only</span>
                        </label>

                        <button
                            className="header-search-btn"
                            disabled={visible}
                            onClick={() => {
                                if (!origin || String(origin).trim() === '') {
                                    toast.error('Please enter departure city', { position: 'top-center' });
                                    return;
                                }
                                if (!destination || String(destination).trim() === '') {
                                    toast.error('Please enter destination city', { position: 'top-center' });
                                    return;
                                }

                                if (String(origin).trim().toLowerCase() === String(destination).trim().toLowerCase()) {
                                    toast.error('Origin and Destination cannot be the same', { position: 'top-center' });
                                    return;
                                }

                                // Advanced check: Same city but different station/airport names
                                if (originData && destinationData) {
                                    const originCity = (originData.city || '').trim().toLowerCase();
                                    const destCity = (destinationData.city || '').trim().toLowerCase();
                                    const originCode = (originData.code || '').trim().toLowerCase();
                                    const destCode = (destinationData.code || '').trim().toLowerCase();

                                    // If city names match OR airport/station codes match, it's the same location effectively
                                    if ((originCity && destCity && originCity === destCity) ||
                                        (originCode && destCode && originCode === destCode)) {
                                        toast.error('Origin and Destination cannot be in the same city', { position: 'top-center' });
                                        return;
                                    }
                                }
                                setNonStopOnly(pendingNonStop);
                                handleSearch({ nonStop: pendingNonStop });
                            }}
                        >
                            Search Routes
                        </button>
                    </div>
                </div>

                {/* Profile & Nav Actions */}
                <div className="navbar-right">
                    {renderNavbarProfile(false)}
                </div>
            </div>
        </nav >
    );
};

export default SearchHeader;
